import { lazy, ComponentType } from 'react';

/**
 * Lazy load a component with retry capability
 * This helps handle chunk loading errors that can occur during deployments
 * or network issues.
 *
 * @param {Function} componentImport - The dynamic import function
 * @param {number} retries - Number of retry attempts (default: 3)
 * @param {number} interval - Delay between retries in ms (default: 1000)
 * @returns {React.LazyExoticComponent} - Lazy loaded component
 */
const lazyWithRetry = <T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  retries = 3,
  interval = 1000
) => {
  return lazy(() => {
    return new Promise<{ default: T }>((resolve, reject) => {
      let attempts = 0;

      const attemptImport = () => {
        componentImport()
          .then(resolve)
          .catch((error) => {
            attempts++;

            // Check if this is a chunk loading error
            const isChunkError =
              error.name === 'ChunkLoadError' ||
              (error.message && (
                error.message.includes('Loading chunk') ||
                error.message.includes('Failed to fetch dynamically imported module')
              ));

            console.error(`Lazy import attempt ${attempts} failed:`, error);

            if (attempts < retries && isChunkError) {
              console.log(`Retrying in ${interval}ms... (attempt ${attempts + 1}/${retries})`);
              setTimeout(attemptImport, interval * attempts); // Exponential backoff
            } else if (isChunkError) {
              // All retries failed, reload the page as last resort
              console.error('All retry attempts failed. Reloading page...');

              // Check if we've reloaded recently to prevent infinite loops
              const lastReload = sessionStorage.getItem('lastChunkErrorReload');
              const now = Date.now();

              if (!lastReload || (now - parseInt(lastReload)) > 10000) {
                sessionStorage.setItem('lastChunkErrorReload', now.toString());
                window.location.reload();
              } else {
                // Don't reload, just reject to show error boundary
                reject(new Error('Failed to load component after multiple retries. Please refresh the page manually.'));
              }
            } else {
              // Not a chunk error, just reject
              reject(error);
            }
          });
      };

      attemptImport();
    });
  });
};

export default lazyWithRetry;
